import { Suspense, lazy, useEffect, useLayoutEffect, useState, type ComponentType, type LazyExoticComponent, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { ApiError, getActiveTenantSlug, hasTenantScopeChoice, opsFetch, setActiveTenantSlug } from "./api";
import ErrorBoundary from "./components/ErrorBoundary";
import BootSkeleton from "./components/BootSkeleton";
import { isChunkLoadError, loadNewBuild, offerNewVersion } from "./lib/chunk-recovery";
import { checkForNewBuild, shouldLoadNewBuild } from "./lib/build-freshness";
import Shell, { ShellFrame } from "./components/Shell";
import { HxSkeleton } from "./components/hx";
import Login from "./pages/Login";
import Logout from "./pages/Logout";
import ForgotPassword from "./pages/ForgotPassword";
import TotpChallenge from "./pages/TotpChallenge";
import TotpEnroll from "./pages/TotpEnroll";
import PasswordChange from "./pages/PasswordChange";
import SectionSkeleton from "./pages/SectionSkeleton";
// Not a page — the shared "where does this role land?" rule, called below.
import { landingFor as sharedLandingFor } from "./landing";
import { SipProvider } from "./pages/voice/SipContext";
import CallBar from "./pages/voice/CallBar";

// ── Route-level code splitting ───────────────────────────────────────────────
// Every page below is loaded on first visit, not at boot. With ~110 pages
// imported statically, a full reload pulled the whole module graph before
// anything rendered — in Vite dev that is well over a thousand requests and
// several seconds (22 Sep 2026); in production it was one large bundle. The
// auth pages, the softphone provider and the call bar stay eager: they are
// on every path or outside the router. <Suspense> around <Routes> shows a
// brief skeleton while a page's chunk arrives.
//
// Every page below goes through lazyPage, not lazy: a chunk fetched on demand
// can be missing, which is what happens to a tab left open across a deploy.
// See lib/chunk-recovery.ts.

/** lazy() that survives a deploy. A page whose chunk no longer exists renders
 *  nothing and a toast offers the reload, so the nav and header stay on screen
 *  instead of the error boundary taking the whole tab. */
// `any` in the constraint mirrors React's own lazy(): a page's props are its
// own business, and narrowing here only fights the two call sites that map a
// named export onto `default`.
function lazyPage<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() => {
    // The server already serves a newer build (lib/build-freshness.ts): this
    // page belongs to the old one and its file may be gone. Load the new build
    // as a plain page load instead of trying — the Suspense skeleton shows
    // until the page goes, so nothing fails on screen.
    if (shouldLoadNewBuild() && loadNewBuild()) return new Promise<never>(() => {});
    const gone = { default: (() => null) as unknown as T };
    return load().then(
      (mod) => {
        if (mod) return mod;
        // Vite RESOLVES with undefined, rather than rejecting, when the
        // vite:preloadError listener in main.tsx cancels the event — which it
        // does on a missing chunk. Handing that undefined to React.lazy
        // crashed on `.default` ("can't access property 'default'", 24 Sep
        // 2026). It is the missing-chunk case.
        offerNewVersion();
        return gone;
      },
      (err: unknown) => {
        if (!isChunkLoadError(err)) throw err;
        offerNewVersion();
        return gone;
      },
    );
  });
}

const AgentStudioV3 = lazyPage(() => import("./pages/agent-studio/AgentStudioV3"));
const Contacts = lazyPage(() => import("./pages/Contacts"));
const AuditLog = lazyPage(() => import("./pages/AuditLog"));
const TenantSettings = lazyPage(() => import("./pages/TenantSettings"));
const Billing = lazyPage(() => import("./pages/Billing"));
const Explore = lazyPage(() => import("./pages/explore/Explore"));
const Dashboards = lazyPage(() => import("./pages/explore/Dashboards"));
const MetricsAdmin = lazyPage(() => import("./pages/explore/MetricsAdmin"));
const ExtractorsAdmin = lazyPage(() => import("./pages/explore/ExtractorsAdmin"));
const DerivationsAdmin = lazyPage(() => import("./pages/explore/DerivationsAdmin"));
const SupportAtAGlance = lazyPage(() => import("./pages/SupportAtAGlance"));
const TenantUsers = lazyPage(() => import("./pages/TenantUsers"));
const Teams = lazyPage(() => import("./pages/Teams"));
const Insights = lazyPage(() => import("./pages/Insights"));
const Profile = lazyPage(() => import("./pages/Profile"));
const Products = lazyPage(() => import("./pages/Products"));
const Dispositions = lazyPage(() => import("./pages/Dispositions"));
const SupportDesk = lazyPage(() => import("./pages/SupportDesk"));
const SupportDeskTickets = lazyPage(() => import("./pages/SupportDeskTickets"));
const SupportDeskNewTicket = lazyPage(() => import("./pages/SupportDeskNewTicket"));

const Dashboard = lazyPage(() => import("./pages/Dashboard"));
const RoleHome = lazyPage(() => import("./pages/RoleHome"));
const IaMatrix = lazyPage(() => import("./pages/IaMatrix"));
const EngageConversations = lazyPage(() => import("./pages/EngageConversations"));
const AnalyticsOverview = lazyPage(() => import("./pages/analytics/AnalyticsOverview"));
const AnalyticsConversations = lazyPage(() => import("./pages/analytics/AnalyticsConversations"));
const AnalyticsConversationDetail = lazyPage(() => import("./pages/analytics/AnalyticsConversationDetail"));
const AnalyticsCallReviews = lazyPage(() => import("./pages/analytics/AnalyticsCallReviews"));
const AnalyticsConfig = lazyPage(() => import("./pages/analytics/AnalyticsConfig"));
const AnalyticsTeamLeader = lazyPage(() => import("./pages/analytics/leadership/TeamLeader"));
const AnalyticsScorecard = lazyPage(() => import("./pages/analytics/leadership/Scorecard"));
const AnalyticsExecutive = lazyPage(() => import("./pages/analytics/leadership/Executive"));
const AnalyticsReports = lazyPage(() => import("./pages/analytics/operations/Reports"));
const AnalyticsAiVsHuman = lazyPage(() => import("./pages/analytics/operations/AiVsHuman"));
const AnalyticsKpiTargets = lazyPage(() => import("./pages/analytics/qa/KpiTargets"));
const AnalyticsAlertThresholds = lazyPage(() => import("./pages/analytics/qa/AlertThresholds"));
const AnalyticsGovernance = lazyPage(() => import("./pages/analytics/qa/Governance"));
const AnalyticsNotificationRules = lazyPage(() => import("./pages/analytics/qa/NotificationRules"));
const AnalyticsIngestion = lazyPage(() => import("./pages/analytics/admin/Ingestion"));
const AnalyticsZendeskImport = lazyPage(() => import("./pages/analytics/admin/ZendeskImport"));
const AnalyticsProperties = lazyPage(() => import("./pages/analytics/admin/Properties"));
const AnalyticsDsar = lazyPage(() => import("./pages/analytics/admin/Dsar"));
const AnalyticsSystemHealth = lazyPage(() => import("./pages/analytics/admin/SystemHealth"));
const AnalyticsRiskInsights = lazyPage(() => import("./pages/analytics/admin/RiskInsights"));
const CxLiveAi = lazyPage(() => import("./pages/cx/CxLiveAi"));
const CxLiveHuman = lazyPage(() => import("./pages/cx/CxLiveHuman"));
const CxGovernance = lazyPage(() => import("./pages/cx/CxGovernance"));
const CxAnalytics = lazyPage(() => import("./pages/cx/CxAnalytics"));
const VoiceOverview = lazyPage(() => import("./pages/voice/VoiceOverview"));
// Merged Versatalk hubs (24 Sep 2026): one page per nav section, the former
// stand-alone pages become tabs. The old routes below are kept for deep links.
const VoiceOverviewHub = lazyPage(() => import("./pages/voice/VoiceOverviewHub"));
const VoiceCalls = lazyPage(() => import("./pages/voice/VoiceCalls"));
const VoiceAgentsHub = lazyPage(() => import("./pages/voice/VoiceAgentsHub"));
const VoiceAdminHub = lazyPage(() => import("./pages/voice/VoiceAdminHub"));
const VoiceSoftphone = lazyPage(() => import("./pages/voice/VoiceSoftphone"));
// The agent's own destinations, and the supervisor and telephony-admin pages
// that came with them. Split like every other page here — the agent desktop
// pulls the softphone chunk, not the whole Versatalk surface.
const ScreenSpec = lazyPage(() => import("./pages/engage/ScreenSpec"));
const AgentTickets = lazyPage(() => import("./pages/voice/AgentTickets"));
const MyPerformance = lazyPage(() => import("./pages/voice/MyPerformance"));
const OutboundPreview = lazyPage(() => import("./pages/voice/OutboundPreview"));
const VoiceAgentStates = lazyPage(() => import("./pages/voice/VoiceAgentStates"));
const VoiceFloor = lazyPage(() => import("./pages/voice/VoiceFloor"));
const VoiceCallbacks = lazyPage(() => import("./pages/voice/VoiceCallbacks"));
const VoiceLiveTranscripts = lazyPage(() => import("./pages/voice/VoiceLiveTranscripts"));
const VoiceVoicemail = lazyPage(() => import("./pages/voice/VoiceVoicemail"));
const VoiceMailboxes = lazyPage(() => import("./pages/voice/VoiceMailboxes"));
const VoiceQueues = lazyPage(() => import("./pages/voice/VoiceQueues"));
const VoiceHours = lazyPage(() => import("./pages/voice/VoiceHours"));
const VoiceDnc = lazyPage(() => import("./pages/voice/VoiceDnc"));
const VoiceWrapSso = lazyPage(() => import("./pages/voice/VoiceWrapSso"));
const VoiceCampaigns = lazyPage(() => import("./pages/voice/VoiceCampaigns"));
const VoiceE911 = lazyPage(() => import("./pages/voice/VoiceE911"));
const VoiceTelephonyPath = lazyPage(() => import("./pages/voice/VoiceTelephonyPath"));
const VoiceInteractions = lazyPage(() => import("./pages/voice/VoiceInteractions"));
const VoiceInteractionDetail = lazyPage(() => import("./pages/voice/VoiceInteractionDetail"));
const VoiceAgents = lazyPage(() => import("./pages/voice/VoiceAgents"));
const VoiceAnalytics = lazyPage(() => import("./pages/voice/VoiceAnalytics"));
const VoiceConfig = lazyPage(() => import("./pages/voice/VoiceConfig"));
const VoiceEngine = lazyPage(() => import("./pages/voice/VoiceEngine"));
const VoiceRecordings = lazyPage(() => import("./pages/voice/VoiceRecordings"));
const VoiceArchived = lazyPage(() => import("./pages/voice/VoiceArchived"));
const VoiceAgentPerformance = lazyPage(() => import("./pages/voice/VoiceAgentPerformance"));
const VoiceFcrGaps = lazyPage(() => import("./pages/voice/VoiceFcrGaps"));
const VoiceTransferInsights = lazyPage(() => import("./pages/voice/VoiceTransferInsights"));
const VoiceBilling = lazyPage(() => import("./pages/voice/VoiceBilling"));
const VoiceSystemHealth = lazyPage(() => import("./pages/voice/VoiceSystemHealth"));
const VoiceOperations = lazyPage(() => import("./pages/voice/VoiceOperations"));
const VoiceNumbers = lazyPage(() => import("./pages/voice/VoiceNumbers"));
const VoiceReports = lazyPage(() => import("./pages/voice/VoiceReports"));
const VoiceConversationIntelligence = lazyPage(() => import("./pages/voice/VoiceConversationIntelligence"));
const VoiceDecisionIntelligence = lazyPage(() => import("./pages/voice/VoiceDecisionIntelligence"));
const VoiceOperationalInsights = lazyPage(() => import("./pages/voice/VoiceOperationalInsights"));
const VoiceEvaluations = lazyPage(() => import("./pages/voice/VoiceEvaluations"));
const VoiceEvaluationAnalytics = lazyPage(() => import("./pages/voice/VoiceEvaluationAnalytics"));
const VoiceQualityAssurance = lazyPage(() => import("./pages/voice/VoiceQualityAssurance"));
const VoiceQaReports = lazyPage(() => import("./pages/voice/VoiceQaReports"));
const ConsoleSessions = lazyPage(() => import("./pages/console/ConsoleSessions"));
const ConsoleSessionDetail = lazyPage(() => import("./pages/console/ConsoleSessionDetail"));
const ConsoleLive = lazyPage(() => import("./pages/console/ConsoleLive"));
const ConsoleCoaching = lazyPage(() => import("./pages/console/ConsoleCoaching"));
const ConsoleConfig = lazyPage(() => import("./pages/console/ConsoleConfig"));
const ConsoleDashboardExecutive = lazyPage(() => import("./pages/console/ConsoleDashboardExecutive"));
const ConsoleDashboardTeamLeader = lazyPage(() => import("./pages/console/ConsoleDashboardTeamLeader"));
const ConsoleDashboardMember = lazyPage(() => import("./pages/console/ConsoleDashboardMember"));
const ConsoleOperationsCommand = lazyPage(() => import("./pages/console/ConsoleOperationsCommand"));
const ConsoleOperationsOutage = lazyPage(() => import("./pages/console/ConsoleOperationsOutage"));
const ConsoleOperationsDispatch = lazyPage(() => import("./pages/console/ConsoleOperationsDispatch"));
const ConsoleOperationsActivation = lazyPage(() => import("./pages/console/ConsoleOperationsActivation"));
const ConsoleScorecard = lazyPage(() => import("./pages/console/ConsoleScorecard"));
const ConsoleTraining = lazyPage(() => import("./pages/console/ConsoleTraining"));
const ConsoleChannels = lazyPage(() => import("./pages/console/ConsoleChannels"));
const ConsoleRouting = lazyPage(() => import("./pages/console/ConsoleRouting"));
const ConsoleTemplates = lazyPage(() => import("./pages/console/ConsoleTemplates"));
const ConsoleInsightsRootCause = lazyPage(() => import("./pages/console/ConsoleInsightsRootCause"));
const ConsoleInsightsCommunity = lazyPage(() => import("./pages/console/ConsoleInsightsCommunity"));
const ConsoleInsightsCallLevel = lazyPage(() => import("./pages/console/ConsoleInsightsCallLevel"));
const ConsoleNotifications = lazyPage(() => import("./pages/console/ConsoleNotifications"));
const ConsoleCompliance = lazyPage(() => import("./pages/console/ConsoleCompliance"));
const WorkflowList = lazyPage(() => import("./pages/workflow/WorkflowList"));
const WorkflowEditor = lazyPage(() => import("./pages/workflow/WorkflowEditor"));
const WorkflowExecutions = lazyPage(() => import("./pages/workflow/WorkflowExecutions"));
const WorkflowIntegrations = lazyPage(() => import("./pages/workflow/WorkflowIntegrations"));
const AnalyticsCoachingList = lazyPage(() => import("./pages/analytics/AnalyticsCoaching").then((m) => ({ default: m.AnalyticsCoachingList })));
const AnalyticsCoachingDetail = lazyPage(() => import("./pages/analytics/AnalyticsCoaching").then((m) => ({ default: m.AnalyticsCoachingDetail })));

export interface Me {
  id: string;
  email: string;
  displayName: string;
  role: "owner" | "admin" | "viewer"; // legacy alias for platformRole
  platformRole: "owner" | "admin" | "viewer";
  // Dynamic role name (from the user's assigned RBAC role) for the header chip.
  roleName?: string | null;
  // Display names for the two platform system roles, resolved from the roles
  // table (so a renamed role like "Platform Staff" → "Account Manager" shows
  // everywhere). Falls back to the static labels if absent.
  platformRoleLabels?: { admin: string; staff: string };
  /** True when this user holds a live-desk extension in the ACTIVE tenant.
   *  Gates the Softphone entry: no extension means no phone and no calls, so
   *  offering the desk would be offering something that cannot work. */
  hasExtension?: boolean;
  // Dynamic-RBAC effective permission set for the ACTIVE tenant scope
  // (governance set when no tenant is selected). Drives nav visibility.
  permissions: string[];
  memberships: Array<{
    tenantId: string;
    slug: string;
    name: string;
    role: "admin" | "user";
    enabledModules: string[];
    billingEnabled?: boolean;
    // Effective permission set when this tenant is the active scope.
    permissions: string[];
    // Dynamic role name the user holds in this workspace.
    roleName?: string | null;
    // Role SLUGS in this workspace — rename-proof; drives the persona
    // landing lens (see landingFor below).
    roleSlugs?: string[];
  }>;
  mustChangePassword: boolean;
  totpEnrolled: boolean;
  /** Holds at least one ADMIN_PERMISSIONS key on a platform role, so /admin
   *  opens for them. Computed server-side so the key list has one definition;
   *  the /admin app uses it to say "this account has no platform access"
   *  rather than navigating in and bouncing off a 403. */
  platformAccess?: boolean;
  /** This SESSION still owes a second factor before it can exercise a
   *  platform-scoped permission (ADMIN_TOTP_REQUIRED). Read together with
   *  totpEnrolled: not enrolled means enrol, enrolled means sign in again,
   *  because a full session has no pre-session to challenge. */
  needsSecondFactor?: boolean;
  // Optional account-meta fields used by /profile. Older /api/status
  // responses without them still parse — the Profile page renders "—"
  // when missing, but the current server populates all three.
  createdAt?: string | null;
  lastLoginAt?: string | null;
  passwordChangedAt?: string | null;
}

interface StatusResponse {
  totpRequired: boolean;
  me: Me | null;
}

type AuthState =
  | { kind: "loading" }
  | { kind: "anon"; totpRequired: boolean }
  | { kind: "authed"; me: Me; totpRequired: boolean };

/** Shown for the instant between a route match and its chunk arriving. The
 *  same shell-shaped skeleton as the boot state, so nothing flashes between
 *  the two and the page lands in place. */
function RouteFallback({ auth, onLoggedOut }: { auth: AuthState; onLoggedOut: () => Promise<void> }) {
  if (auth.kind === "anon") return <AuthBoot />;
  if (auth.kind !== "authed") return <BootSkeleton />;
  return (
    <Shell me={auth.me} onLoggedOut={onLoggedOut}>
      <div className="work-inner">
        <HxSkeleton variant="page" />
      </div>
    </Shell>
  );
}

// ── One persistent chrome around the routed pages ────────────────────────────
// Every page renders <Shell>, which used to draw the whole chrome — so each
// navigation rebuilt the header and nav rail from scratch (state reset, tenant
// list refetched, open group re-animated). The chrome is now mounted ONCE here
// as <ShellFrame>; a page's <Shell> inside it renders only its section bar and
// work area. Routes that draw no Helix chrome stay outside the frame:
//   - the auth pages (login / TOTP / password change) — no session to draw;
//   - the Analytics and Console modules — each has its own full-screen shell
//     (AnalyticsShell / ConsoleShell, ported from the source products).
// A <Shell> mounted outside the frame still draws the full chrome itself, so a
// route wrongly left out of the frame degrades to the old behaviour, not to a
// missing nav.
const BARE_PATHS = new Set(["/login", "/login/forgot", "/login/totp", "/totp/enroll", "/password/change"]);
const BARE_PREFIXES = ["/analytics", "/console"];
function isFramedPath(pathname: string): boolean {
  if (BARE_PATHS.has(pathname)) return false;
  return !BARE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}
function Chrome({ me, onLoggedOut, children }: { me: Me | null; onLoggedOut: () => Promise<void>; children: ReactNode }) {
  if (!me) return <>{children}</>;
  return <ShellFrame me={me} onLoggedOut={onLoggedOut}>{children}</ShellFrame>;
}

// The last profile the server confirmed, kept for THIS browser tab so a full
// page reload can paint the real shell — header, your nav — at once instead of
// a placeholder while /ops/api/status is re-checked. It is only ever used to
// draw the frame around a loading skeleton; nothing renders from it and the
// server confirms (or rejects) the session before any page shows. Cleared the
// moment the server says the session is gone.
const ME_KEY = "chloe-ops:last-me";
// Whether this BROWSER was signed in when it last asked — not who, just yes or
// no, so it can live in localStorage and reach a fresh tab. It only picks the
// boot placeholder: the app-shell skeleton for someone about to land inside
// the app, the sign-in canvas for someone about to land on /login. Without it
// every signed-out visit (e.g. "Sign in" on the marketing page) flashed the
// empty app shell — nav rail, grey cards — before the login page (24 Sep 2026).
const SIGNED_IN_HINT_KEY = "helix-ops:signed-in";
function readSignedInHint(): boolean {
  try {
    return window.localStorage.getItem(SIGNED_IN_HINT_KEY) === "1";
  } catch {
    return false;
  }
}
function readRememberedMe(): Me | null {
  try {
    const raw = window.sessionStorage.getItem(ME_KEY);
    return raw ? (JSON.parse(raw) as Me) : null;
  } catch {
    return null;
  }
}
function rememberMe(me: Me | null): void {
  try {
    if (me) window.sessionStorage.setItem(ME_KEY, JSON.stringify(me));
    else window.sessionStorage.removeItem(ME_KEY);
  } catch {
    /* storage unavailable — the placeholder shell is shown instead */
  }
}
function rememberSignedIn(signedIn: boolean): void {
  try {
    if (signedIn) window.localStorage.setItem(SIGNED_IN_HINT_KEY, "1");
    else window.localStorage.removeItem(SIGNED_IN_HINT_KEY);
  } catch {
    /* storage unavailable — a fresh tab shows the sign-in canvas */
  }
}

/** The sign-in page's own dark canvas, empty — the boot placeholder for
 *  someone who is (probably) signed out, so the login form lands on the same
 *  background instead of replacing a flash of the app shell. auth.css comes
 *  in with the eagerly-imported auth pages. */
function AuthBoot({ label = "Loading" }: { label?: string }) {
  return <div className="hxa" role="status" aria-busy="true" aria-label={label} />;
}

/** Guards the default-scope pick to ONCE per page load. hasTenantScopeChoice()
 *  is the real condition, but it reads localStorage, which can be unavailable
 *  (private window, blocked site data) — and there the write below is a no-op,
 *  so the condition would never become false and the re-check it triggers
 *  would loop. This flag ends that possibility outright. */
let defaultScopePicked = false;

/** First workspace in the switcher's own order — the one the list shows at
 *  the top. Returns whether the scope actually changed: false if the list is
 *  empty, the call failed, or the write didn't stick. Failure is not fatal —
 *  no scope just means the platform view, the state /ops has always started
 *  in. */
async function pickDefaultWorkspace(): Promise<boolean> {
  try {
    const r = await opsFetch<{ tenants: Array<{ slug: string }> }>("/ops/api/tenants");
    const first = r.tenants?.[0];
    if (!first?.slug) return false;
    setActiveTenantSlug(first.slug);
    // Read it back: storage may have refused the write.
    return getActiveTenantSlug() === first.slug;
  } catch {
    return false;
  }
}

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ kind: "loading" });
  const [rememberedMe] = useState<Me | null>(() => readRememberedMe());
  const location = useLocation();
  const navigate = useNavigate();

  // Zero-arg on purpose: it is handed to pages as onLoggedOut and may be
  // called from event handlers, so the retry counter lives on the inner call.
  async function refreshAuth(): Promise<void> {
    return attemptRefresh(0);
  }
  async function attemptRefresh(attempt: number): Promise<void> {
    try {
      const status = await opsFetch<StatusResponse>("/ops/api/status");
      if (status.me) {
        // Ensure a non-owner has a VALID active tenant scope before setting
        // the auth state. The active-tenant slug persists in localStorage
        // across sessions, so a slug left over from a previous user (e.g. an
        // owner scoped to a workspace this tenant admin isn't a member of)
        // would otherwise stick — the slug header wouldn't resolve and every
        // tenant-scoped API call 403s with "tenant_scope_required". Auto-pick
        // their first membership whenever the current slug is missing or not
        // one they belong to. Owners explicitly opt into "all tenants", so we
        // leave their (possibly empty) scope alone.
        const isOwner = (status.me.platformRole ?? status.me.role) === "owner";
        const memberships = status.me.memberships ?? [];
        if (!isOwner && memberships.length > 0) {
          const current = getActiveTenantSlug();
          const valid = current != null && memberships.some((m) => m.slug === current);
          if (!valid) {
            setActiveTenantSlug(memberships[0].slug);
          }
        }
        // An owner with no scope yet lands on a workspace rather than on the
        // cross-tenant view, where Home has nothing to draw and says "pick a
        // tenant from the top bar" — an empty page as the first thing after
        // signing in. Only when NO choice has been made on this browser: a
        // deliberate "All workspaces" is remembered (api.ts) and left alone,
        // and logout forgets the choice so the next sign-in defaults again.
        // Done here, before any page mounts, so nothing fetches unscoped and
        // then has to refetch.
        if (isOwner && !defaultScopePicked && !hasTenantScopeChoice()) {
          defaultScopePicked = true;
          if (await pickDefaultWorkspace()) {
            // ASK AGAIN. The payload in hand was built for the scope this
            // request carried, which was none: `me.permissions` is the
            // platform governance set, and an owner holds no memberships, so
            // activePermissions() has nothing tenant-shaped to fall back on
            // (permissions.ts) — the rail would paint Home/Billing/Platform
            // and nothing else until the next reload. That is exactly what
            // the first sign-in showed (23 Sep 2026). The switcher never hit
            // this because changing workspace there reloads the page.
            return attemptRefresh(attempt);
          }
        }
        rememberMe(status.me);
        rememberSignedIn(true);
        setAuth({ kind: "authed", me: status.me, totpRequired: status.totpRequired });
      } else {
        // Signed out: the next account to sign in on this tab gets the
        // default-workspace pick of its own (logout clears the stored choice
        // in Shell.tsx, so only this once-per-load guard would stand in the
        // way).
        defaultScopePicked = false;
        rememberMe(null);
        rememberSignedIn(false);
        setAuth({ kind: "anon", totpRequired: status.totpRequired });
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        rememberMe(null);
        rememberSignedIn(false);
        setAuth({ kind: "anon", totpRequired: true });
        return;
      }
      // Network blip or server error — the session is NOT gone (it lives in
      // the database), the server is just unreachable for a moment: a dev
      // restart, a deploy rolling over, a proxy hiccup. This used to drop to
      // the login page at once, so every server restart looked like being
      // signed out (22 Sep 2026). Keep whatever is on screen and try again,
      // backing off to a few seconds, for up to about a minute.
      if (attempt < 20) {
        const delay = Math.min(4000, 500 * (attempt + 1));
        window.setTimeout(() => void attemptRefresh(attempt + 1), delay);
        return;
      }
      setAuth((cur) => (cur.kind === "authed" ? cur : { kind: "anon", totpRequired: true }));
    }
  }

  useEffect(() => {
    refreshAuth();
  }, []);

  // Drop to anonymous WITHOUT a server round trip. The /logout route needs the
  // auth state to read "anon" before it navigates, because /login renders
  // <Navigate to="/" replace /> while it still reads "authed" — the router would
  // bounce a signing-out user straight back into the product. Re-probing the
  // server instead would cost another slow round trip and could still race the
  // revocation it is asking about.
  function markSignedOut() {
    setAuth({ kind: "anon", totpRequired: true });
  }
  // Each navigation is a moment a deploy could bite; ask (throttled) whether
  // one has happened, so the NEXT page loads the new build cleanly.
  useEffect(() => {
    void checkForNewBuild();
  }, [location.pathname]);

  // Forced post-auth redirects: must-change-password supersedes everything.
  useEffect(() => {
    if (auth.kind !== "authed") return;
    const path = location.pathname;
    if (auth.me.mustChangePassword && path !== "/password/change") {
      navigate("/password/change", { replace: true });
      return;
    }
    if (auth.totpRequired && !auth.me.totpEnrolled && path !== "/totp/enroll" && path !== "/password/change") {
      navigate("/totp/enroll", { replace: true });
    }
  }, [auth, location.pathname, navigate]);

  const framed = isFramedPath(location.pathname);
  // Who the chrome is drawn for. While the session is being re-checked on a
  // reload, the remembered profile paints the real frame straight away; the
  // server's answer then swaps in at the SAME tree position, so the frame
  // mounted during boot is the one every page then renders into.
  const frameMe: Me | null =
    !framed ? null : auth.kind === "authed" ? auth.me : auth.kind === "loading" ? rememberedMe : null;

  // A genuinely fresh tab (or a reload on an unframed page) sees a placeholder:
  // the app shell only if this browser was signed in last time (or the path
  // is an app page and a profile is remembered); otherwise the sign-in canvas,
  // since /login is where a signed-out visit ends up.
  const expectApp = !BARE_PATHS.has(location.pathname) && (rememberedMe != null || readSignedInHint());

  // The same choice, made once more for <body>. index.html adds `hx-preauth`
  // to <html> before the bundle loads so the page is dark from its first
  // paint whenever the sign-in canvas is what will show (otherwise a white
  // body flashed between the dark marketing page and the dark login page).
  // Kept in step here — on while anything renders on that canvas, off before
  // the light app frame paints — so signing in never leaves a dark body
  // behind the app. Layout effect: it must flip before the browser paints.
  const onAuthCanvas = auth.kind === "anon" || (auth.kind === "loading" && !frameMe && !expectApp);
  useLayoutEffect(() => {
    document.documentElement.classList.toggle("hx-preauth", onAuthCanvas);
  }, [onAuthCanvas]);

  if (auth.kind === "loading" && !frameMe) {
    return expectApp ? <BootSkeleton label="Signing you in" /> : <AuthBoot label="Signing you in" />;
  }

  return (
    <>
      <ErrorBoundary>
      {/* SipProvider wraps the ROUTER, not a page. The softphone's UserAgent has
          to outlive route changes — mounted inside a page, its cleanup ran on
          every navigation and hung up the live call. See SipContext.tsx. */}
      <SipProvider>
      {/* Chrome likewise wraps the router: the header + nav rail outlive route
          changes, and only the page's section bar + work area re-render. */}
      <Chrome me={frameMe} onLoggedOut={refreshAuth}>
      {auth.kind === "loading" ? (
        <div className="work">
          <div className="work-inner">
            <HxSkeleton variant="page" label="Signing you in" />
          </div>
        </div>
      ) : (
      <Suspense fallback={<RouteFallback auth={auth} onLoggedOut={refreshAuth} />}>
      <Routes>
        <Route
          path="/login"
          element={
            auth.kind === "authed" ? (
              <Navigate to="/" replace />
            ) : (
              <Login onAuthChanged={refreshAuth} />
            )
          }
        />
        {/* SIGN OUT AS A ROUTE. Deliberately NOT gated on auth.kind: this must
            work from any state, including the one where the SPA still believes
            it is authed. Gating it the way /login is gated would reintroduce
            exactly the bounce that made the sign-out button read as dead. */}
        <Route path="/logout" element={<Logout onSignedOut={markSignedOut} />} />
        <Route
          path="/login/forgot"
          element={
            auth.kind === "authed" ? (
              <Navigate to="/" replace />
            ) : (
              <ForgotPassword />
            )
          }
        />
        <Route
          path="/login/totp"
          element={
            auth.kind === "authed" ? (
              <Navigate to="/" replace />
            ) : (
              <TotpChallenge onAuthChanged={refreshAuth} />
            )
          }
        />
        <Route
          path="/totp/enroll"
          element={
            auth.kind === "authed" ? (
              <TotpEnroll me={auth.me} onEnrolled={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/password/change"
          element={
            auth.kind === "authed" ? (
              <PasswordChange me={auth.me} onChanged={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Helix·CX new shell — Home + section skeletons. Any authed user
            reaches them, so the new IA is fully navigable while sections get
            filled in. Access is the server's answer: every /ops/api/* route
            carries its own permission gate. */}

        {/* HOME IS THE DASHBOARD, EVERYWHERE.
            Reverses 40d275ba (26 Aug), which made Home the ROLE page on the
            reasoning that "what am I allowed to do here" is the question
            everyone has on signing in. That argument still reads well; it lost
            to a plainer one — Home must be the same screen on every
            environment, and dev has been showing the dashboard all along.
            Two different Home pages depending on where you are logged in is
            its own small confusion, and it is the kind that wastes a
            conversation every time somebody shares a screenshot.
            The role page is not deleted — it is at /home/role below. */}
        <Route
          path="/home"
          element={
            auth.kind === "authed" ? (
              <Dashboard me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* The role page 40d275ba built: the role, where it lands, every
            destination it reaches, and what it never sees — all derived from
            the same destinationsFor() the nav rail renders from. Kept because
            it answers a real question and nothing else answers it; it is simply
            no longer what Home means. */}
        <Route
          path="/home/role"
          element={
            auth.kind === "authed" ? (
              <RoleHome me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* IA coverage matrix — reachable from the floating button on every
            page. The route is gated by the endpoint behind it (roles.manage),
            so a deep link from someone without it renders an explicit refusal
            rather than an empty grid. */}
        <Route
          path="/ia-matrix"
          element={
            auth.kind === "authed" ? (
              <IaMatrix me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Same dashboard as /home, kept so links minted while the role page
            held Home still land somewhere sensible rather than 404. */}
        <Route
          path="/home/dashboard"
          element={
            auth.kind === "authed" ? (
              <Dashboard me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/skeleton/:id"
          element={
            auth.kind === "authed" ? (
              <SectionSkeleton me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/support"
          element={
            auth.kind === "authed" ? (
              <SupportAtAGlance me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* /conversations IS THE INBOX, on every environment.
            It briefly held the design handoff's spec view of this screen, with
            the working inbox moved to /conversations/workspace. dev never had
            that, so the same URL opened a page of region placeholders here and
            the real workspace there — the same split that had Home showing two
            different screens, and the same cost: the nav's most-used entry led
            somewhere you then had to click again to leave.

            The spec view is kept at /conversations/spec, alongside the one the
            agent screens already use (/voice/agent/my-performance/spec).

              /conversations             the inbox
              /conversations/spec        the handoff's spec view
              /conversations/workspace   the inbox (kept — links exist to it)
              /conversations/:id         a specific conversation

            Order matters — every literal route must be declared before `:id`,
            or react-router matches it as a conversation id and
            EngageConversations goes looking for a record called "spec". */}
        <Route
          path="/conversations"
          element={
            auth.kind === "authed" ? (
              <EngageConversations me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/conversations/spec"
          element={
            auth.kind === "authed" ? (
              <ScreenSpec me={auth.me} onLoggedOut={refreshAuth} screen="conversations" />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/conversations/workspace"
          element={
            auth.kind === "authed" ? (
              <EngageConversations me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/conversations/:id"
          element={
            auth.kind === "authed" ? (
              <EngageConversations me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Old Agent Studio path — superseded by Agent Studio v3 at /studio
            (which carries its own auth + section gate). The page file stays:
            AgentStudioV3 imports ToolsTab etc. from pages/AgentStudio.tsx. */}
        <Route path="/agent/:section?" element={<Navigate to="/studio" replace />} />
        <Route
          path="/studio/:agentId?/:group?"
          element={
            auth.kind === "authed" ? (
              <AgentStudioV3 me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route path="/agent" element={<Navigate to="/agent/overview" replace />} />
        <Route
          path="/contacts/:email?"
          element={
            auth.kind === "authed" ? (
              <Contacts me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/audit"
          element={
            auth.kind === "authed" ? (
              <AuditLog me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Tenant-scoped settings for Platform Staff — manage their own
            tenant's brand/config, integrations, API keys, domains, and
            users (no cross-tenant list, no owner-only Modules tab). */}
        <Route
          path="/tenant-settings/:section?"
          element={
            auth.kind === "authed" ? (
              <TenantSettings me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Real-time billing dashboard — visible to anyone scoped to a
            tenant (a tenant admin sees their own bill); rate-card edits are
            staff-gated server-side. */}
        <Route
          path="/billing"
          element={
            auth.kind === "authed" ? (
              <Billing me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Metrics & Extractors platform — Explorer + Dashboards (Phase 3/4).
            Available to any tenant-scoped user; server enforces tenant scope. */}
        <Route
          path="/explore"
          element={auth.kind === "authed" ? <Explore me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/dashboards"
          element={auth.kind === "authed" ? <Dashboards me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/metrics"
          element={auth.kind === "authed" ? <MetricsAdmin me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/extractors"
          element={auth.kind === "authed" ? <ExtractorsAdmin me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/derivations"
          element={auth.kind === "authed" ? <DerivationsAdmin me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />}
        />
        {/* /agents and /agents/:id both forward to the unified Agent
            Studio. The Studio's first column is the agents list, so
            there's no separate page to maintain. */}
        <Route path="/agents" element={<Navigate to="/agent/overview" replace />} />
        <Route path="/agents/:id" element={<Navigate to="/agent/overview" replace />} />
        <Route
          path="/users"
          element={
            auth.kind === "authed" ? (
              <TenantUsers me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/teams"
          element={
            auth.kind === "authed" ? (
              <Teams me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/insights"
          element={
            auth.kind === "authed" ? (
              <Insights me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/profile"
          element={
            auth.kind === "authed" ? (
              <Profile me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* The product launcher (Sunday 1731): every member, like Profile. */}
        <Route
          path="/products"
          element={
            auth.kind === "authed" ? (
              <Products me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Support Desk — the ITSM surface. Tickets (list + create) are agent
            pages (conversations.read/write); the configuration sections each
            live at /support-desk/:tab (support_desk.manage). The sidebar's
            Support Desk group lists them one below the other. */}
        <Route
          path="/support-desk/tickets/new"
          element={
            auth.kind === "authed" ? (
              <SupportDeskNewTicket me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* The ticket workspace: list + selected ticket in one split view;
            /support-desk/tickets/:ticketId deep-links a ticket. */}
        <Route
          path="/support-desk/tickets/:ticketId?"
          element={
            auth.kind === "authed" ? (
              <SupportDeskTickets me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/support-desk/:tab?"
          element={
            auth.kind === "authed" ? (
              <SupportDesk me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Dispositions & Tags manager (dispositions.manage). */}
        <Route
          path="/dispositions"
          element={
            auth.kind === "authed" ? (
              <Dispositions me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Analytics module — gated by section="analytics" (requireModule on server).
            Parent-child structure mirrors gzp-versight's menu. AnalyticsShell
            renders a left sub-rail that's always visible on every analytics
            sub-page. New stubs render a ComingSoon placeholder until each
            page lands in its corresponding phase per ANALYTICS-PORT-PLAN.md. */}
        <Route
          path="/analytics"
          element={
            auth.kind === "authed" ? (
              <AnalyticsOverview me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Leadership */}
        <Route
          path="/analytics/leadership/executive"
          element={
            auth.kind === "authed" ? (
              <AnalyticsExecutive me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/leadership/team-leader"
          element={
            auth.kind === "authed" ? (
              <AnalyticsTeamLeader me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/leadership/scorecard"
          element={
            auth.kind === "authed" ? (
              <AnalyticsScorecard me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/leadership/coaching"
          element={
            auth.kind === "authed" ? (
              <AnalyticsCoachingList me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/leadership/coaching/:sessionId"
          element={
            auth.kind === "authed" ? (
              <AnalyticsCoachingDetail me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Operations */}
        <Route
          path="/analytics/operations/call-investigation"
          element={
            auth.kind === "authed" ? (
              <AnalyticsConversations me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/operations/call-investigation/:id"
          element={
            auth.kind === "authed" ? (
              <AnalyticsConversationDetail me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/operations/reports"
          element={
            auth.kind === "authed" ? (
              <AnalyticsReports me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/operations/ai-vs-human"
          element={
            auth.kind === "authed" ? (
              <AnalyticsAiVsHuman me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/operations/reviews"
          element={
            auth.kind === "authed" ? (
              <AnalyticsCallReviews me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* QA Admin */}
        <Route
          path="/analytics/qa/kpi-targets"
          element={
            auth.kind === "authed" ? (
              <AnalyticsKpiTargets me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/qa/alert-thresholds"
          element={
            auth.kind === "authed" ? (
              <AnalyticsAlertThresholds me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/qa/governance"
          element={
            auth.kind === "authed" ? (
              <AnalyticsGovernance me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/qa/notifications"
          element={
            auth.kind === "authed" ? (
              <AnalyticsNotificationRules me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Administration */}
        <Route
          path="/analytics/admin/ingestion"
          element={
            auth.kind === "authed" ? (
              <AnalyticsIngestion me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/admin/zendesk"
          element={
            auth.kind === "authed" ? (
              <AnalyticsZendeskImport me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/admin/ai-models"
          element={
            auth.kind === "authed" ? (
              <AnalyticsConfig me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/admin/properties"
          element={
            auth.kind === "authed" ? (
              <AnalyticsProperties me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/admin/dsar"
          element={
            auth.kind === "authed" ? (
              <AnalyticsDsar me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/admin/system-health"
          element={
            auth.kind === "authed" ? (
              <AnalyticsSystemHealth me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/analytics/admin/risk-insights"
          element={
            auth.kind === "authed" ? (
              <AnalyticsRiskInsights me={auth.me} onLoggedOut={refreshAuth} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Back-compat redirects from the pre-Phase-A flat URLs. Bookmarks
            and in-app cross-links to the old paths land on the new ones. */}
        <Route path="/analytics/conversations" element={<Navigate to="/analytics/operations/call-investigation" replace />} />
        <Route path="/analytics/conversations/:id" element={<RedirectConvDetail />} />
        <Route path="/analytics/reviews" element={<Navigate to="/analytics/operations/reviews" replace />} />
        <Route path="/analytics/coaching" element={<Navigate to="/analytics/leadership/coaching" replace />} />
        <Route path="/analytics/coaching/:sessionId" element={<RedirectCoachingDetail />} />
        <Route path="/analytics/config" element={<Navigate to="/analytics/admin/ai-models" replace />} />
        {/* Voice module — gated by section="voice" (requireModule on server).
            Parent-child structure mirrors gzp-versatalk's menu. VoiceShell
            renders a left sub-rail visible on every voice sub-page. New
            stubs render VoiceComingSoon until each lands per VOICE-PORT-PLAN.md. */}
        <Route path="/voice" element={auth.kind === "authed" ? <VoiceOverviewHub me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/dashboard" element={auth.kind === "authed" ? <VoiceOverview me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/calls" element={auth.kind === "authed" ? <VoiceCalls me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/agents" element={auth.kind === "authed" ? <VoiceAgentsHub me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/admin" element={auth.kind === "authed" ? <VoiceAdminHub me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/performance" element={auth.kind === "authed" ? <VoiceAnalytics me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Operations */}
        <Route path="/voice/softphone" element={auth.kind === "authed" ? <VoiceSoftphone me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* The agent's other four destinations from the design (roles.js). Real
            routes rather than dead nav entries: each names what the screen will
            be and which backlog item is in the way. See AgentPending.tsx for why
            they are visible instead of hidden. */}
        <Route path="/voice/agent/tickets" element={auth.kind === "authed" ? <AgentTickets me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/agent/tickets/spec" element={auth.kind === "authed" ? <ScreenSpec me={auth.me} onLoggedOut={refreshAuth} screen="tickets" /> : <Navigate to="/login" replace />} />
        <Route path="/voice/agent/voicemail" element={auth.kind === "authed" ? <VoiceVoicemail me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/agent/outbound-preview" element={auth.kind === "authed" ? <OutboundPreview me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/agent/outbound-preview/spec" element={auth.kind === "authed" ? <ScreenSpec me={auth.me} onLoggedOut={refreshAuth} screen="outbound-preview" /> : <Navigate to="/login" replace />} />
        <Route path="/voice/agent/my-performance" element={auth.kind === "authed" ? <MyPerformance me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/agent/my-performance/spec" element={auth.kind === "authed" ? <ScreenSpec me={auth.me} onLoggedOut={refreshAuth} screen="my-performance" /> : <Navigate to="/login" replace />} />
        {/* /voice/softphone-agents is gone. Extensions are allocated when a user
            is created with "Live-desk agent" ticked and released when they are
            removed, so there is nothing to manage here. Redirected rather than
            dropped, because the old path is bookmarked and in muscle memory —
            Settings → Users is where extensions are now visible. */}
        <Route path="/voice/softphone-agents" element={<Navigate to="/tenant-users" replace />} />
        <Route path="/voice/operations/live" element={auth.kind === "authed" ? <VoiceInteractions me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/operations/recordings" element={auth.kind === "authed" ? <VoiceRecordings me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/operations/archived" element={auth.kind === "authed" ? <VoiceArchived me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Agent Analytics */}
        <Route path="/voice/agents/performance" element={auth.kind === "authed" ? <VoiceAgentPerformance me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/agents/fcr" element={auth.kind === "authed" ? <VoiceFcrGaps me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/agents/list" element={auth.kind === "authed" ? <VoiceAgents me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/agents/transfers" element={auth.kind === "authed" ? <VoiceTransferInsights me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Intelligence */}
        <Route path="/voice/intelligence/conversation" element={auth.kind === "authed" ? <VoiceConversationIntelligence me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/intelligence/decision" element={auth.kind === "authed" ? <VoiceDecisionIntelligence me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/intelligence/operational" element={auth.kind === "authed" ? <VoiceOperationalInsights me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Quality Assurance */}
        <Route path="/voice/qa/evaluations" element={auth.kind === "authed" ? <VoiceEvaluations me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/qa/evaluation-analytics" element={auth.kind === "authed" ? <VoiceEvaluationAnalytics me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/qa/quality" element={auth.kind === "authed" ? <VoiceQualityAssurance me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/qa/reports" element={auth.kind === "authed" ? <VoiceQaReports me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Admin */}
        <Route path="/voice/admin/billing" element={auth.kind === "authed" ? <VoiceBilling me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/admin/system-health" element={auth.kind === "authed" ? <VoiceSystemHealth me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/admin/reports" element={auth.kind === "authed" ? <VoiceReports me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/admin/config" element={auth.kind === "authed" ? <VoiceConfig me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Platform-staff only — the page itself renders "Not available." for a
            tenant, and every endpoint behind it needs a platform-only permission. */}
        <Route path="/voice/admin/engine" element={auth.kind === "authed" ? <VoiceEngine me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/admin/operations" element={auth.kind === "authed" ? <VoiceOperations me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/admin/numbers" element={auth.kind === "authed" ? <VoiceNumbers me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/admin/queues" element={auth.kind === "authed" ? <VoiceQueues me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/admin/hours" element={auth.kind === "authed" ? <VoiceHours me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/admin/dnc" element={auth.kind === "authed" ? <VoiceDnc me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/admin/wrap" element={auth.kind === "authed" ? <VoiceWrapSso me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Mailbox CONFIG, split out of /voice/agent/voicemail — see the note at
            the top of VoiceMailboxes.tsx. It sits beside Numbers, Queues, Hours
            and Wrap-up because it answers the same question they do: where a
            call goes. The agent route keeps the inbox. */}
        <Route path="/voice/admin/voicemail" element={auth.kind === "authed" ? <VoiceMailboxes me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/admin/campaigns" element={auth.kind === "authed" ? <VoiceCampaigns me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/supervisor/floor" element={auth.kind === "authed" ? <VoiceFloor me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/supervisor/callbacks" element={auth.kind === "authed" ? <VoiceCallbacks me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/supervisor/live" element={auth.kind === "authed" ? <VoiceLiveTranscripts me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/supervisor/agents" element={auth.kind === "authed" ? <VoiceAgentStates me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/admin/e911" element={auth.kind === "authed" ? <VoiceE911 me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/voice/admin/telephony-path" element={auth.kind === "authed" ? <VoiceTelephonyPath me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Interaction detail keeps its existing URL pattern */}
        <Route path="/voice/interactions/:id" element={auth.kind === "authed" ? <VoiceInteractionDetail me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Back-compat redirects from the pre-Phase-A flat URLs. */}
        <Route path="/voice/interactions" element={<Navigate to="/voice/operations/live" replace />} />
        <Route path="/voice/analytics" element={<Navigate to="/voice/performance" replace />} />
        <Route path="/voice/config" element={<Navigate to="/voice/admin/config" replace />} />
        {/* CX control plane — gated by section="cx" (requireModule('cx') on server).
            S2, the agent workspace, as TWO surfaces: watching the AI work
            (explain + override) and assisting a human on the call (suggest +
            act). Comcast scores them under different lenses — see
            docs/comcast-demo-screens-spec.md §5a. */}
        <Route path="/cx/live/ai/:id?" element={auth.kind === "authed" ? <CxLiveAi me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/cx/live/human/:id?" element={auth.kind === "authed" ? <CxLiveHuman me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* The old single-console route — kept so no bookmark or doc link dies. */}
        <Route path="/cx/workspace" element={<Navigate to="/cx/live/ai" replace />} />
        {/* Governance & explainability — live policies + decision traces (§3.2/§3.6). */}
        <Route path="/cx/governance" element={auth.kind === "authed" ? <CxGovernance me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/cx/analytics" element={auth.kind === "authed" ? <CxAnalytics me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Workflow module — gated by section="workflow" (requireModule on server). */}
        <Route path="/workflow" element={auth.kind === "authed" ? <WorkflowList me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/workflow/editor/:id" element={auth.kind === "authed" ? <WorkflowEditor me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/workflow/executions" element={auth.kind === "authed" ? <WorkflowExecutions me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/integrations" element={auth.kind === "authed" ? <WorkflowIntegrations me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />

        {/* Console module — gated by section="console" (requireModule on server).
            Parent-child structure mirrors gzp-versense's menu. ConsoleShell
            renders a left sub-rail on every console sub-page. Stubs render
            ConsoleComingSoon per CONSOLE-PORT-PLAN.md until each ships. */}
        <Route path="/console" element={auth.kind === "authed" ? <ConsoleLive me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Dashboard */}
        <Route path="/console/dashboard/executive" element={auth.kind === "authed" ? <ConsoleDashboardExecutive me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/console/dashboard/team-leader" element={auth.kind === "authed" ? <ConsoleDashboardTeamLeader me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/console/dashboard/member" element={auth.kind === "authed" ? <ConsoleDashboardMember me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Operations Center */}
        <Route path="/console/operations/command" element={auth.kind === "authed" ? <ConsoleOperationsCommand me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/console/operations/outage" element={auth.kind === "authed" ? <ConsoleOperationsOutage me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/console/operations/dispatch" element={auth.kind === "authed" ? <ConsoleOperationsDispatch me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/console/operations/activation" element={auth.kind === "authed" ? <ConsoleOperationsActivation me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Agent Performance */}
        <Route path="/console/performance/scorecard" element={auth.kind === "authed" ? <ConsoleScorecard me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/console/performance/coaching" element={auth.kind === "authed" ? <ConsoleCoaching me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/console/performance/training" element={auth.kind === "authed" ? <ConsoleTraining me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Message Hub — Unified Inbox repoints to /conversations (no duplicate inbox) */}
        <Route path="/console/messages/inbox" element={<Navigate to="/conversations" replace />} />
        <Route path="/console/messages/channels" element={auth.kind === "authed" ? <ConsoleChannels me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/console/messages/routing" element={auth.kind === "authed" ? <ConsoleRouting me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/console/messages/templates" element={auth.kind === "authed" ? <ConsoleTemplates me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Insights */}
        <Route path="/console/insights/root-cause" element={auth.kind === "authed" ? <ConsoleInsightsRootCause me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/console/insights/community" element={auth.kind === "authed" ? <ConsoleInsightsCommunity me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/console/insights/call-level" element={auth.kind === "authed" ? <ConsoleInsightsCallLevel me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Settings */}
        <Route path="/console/settings/integrations" element={auth.kind === "authed" ? <ConsoleConfig me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/console/settings/notifications" element={auth.kind === "authed" ? <ConsoleNotifications me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/console/settings/compliance" element={auth.kind === "authed" ? <ConsoleCompliance me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Existing session pages */}
        <Route path="/console/sessions" element={auth.kind === "authed" ? <ConsoleSessions me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        <Route path="/console/sessions/:id" element={auth.kind === "authed" ? <ConsoleSessionDetail me={auth.me} onLoggedOut={refreshAuth} /> : <Navigate to="/login" replace />} />
        {/* Back-compat redirects from pre-port flat URLs */}
        <Route path="/console/live" element={<Navigate to="/console" replace />} />
        <Route path="/console/coaching" element={<Navigate to="/console/performance/coaching" replace />} />
        <Route path="/console/config" element={<Navigate to="/console/settings/integrations" replace />} />
        {/* Landing lens — the persona decides where "/" drops you (personas ×
            panes matrix, docs/architecture/ops-personas-matrix.md):
              - Agent (tenant-user) / Team Lead → /conversations (the queue IS
                their day; the lead's Team view is one click in the inbox)
              - everyone else (manager, executive, admin, platform roles)
                → /home (the rollup dashboard)
            Keyed off role SLUGS (rename-proof), resolved for the active
            workspace. Deep links are untouched — this only decides "/". */}
        <Route
          path="/"
          element={auth.kind === "authed" ? <Navigate to={landingFor(auth.me)} replace /> : <Navigate to="/login" replace />}
        />
        <Route
          path="*"
          element={auth.kind === "authed" ? <Navigate to={landingFor(auth.me)} replace /> : <Navigate to="/login" replace />}
        />
      </Routes>
      </Suspense>
      )}
      </Chrome>
      {/* In-call controls on EVERY page. Inside SipProvider so it reads the same
          session; outside <Routes> so it isn't unmounted by navigation. */}
      <CallBar />
      </SipProvider>
      </ErrorBoundary>
    </>
  );
}

// Back-compat redirects for the pre-Phase-A flat analytics URLs that
// carry a route param. Plain string Navigate handles paramless paths;
// these forward the param into the new nested URL.
// Persona landing lens — moved to ./landing so the Shell's "Back to <role>"
// button resolves it the same way this redirect does. It used the first nav
// leaf as a stand-in, which is Home for everyone, so an agent's "Back to Agent"
// did not return them to their desk.
function landingFor(me: Me): string {
  return sharedLandingFor(me);
}

function RedirectConvDetail() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/analytics/operations/call-investigation/${id ?? ""}`} replace />;
}

function RedirectCoachingDetail() {
  const { sessionId } = useParams<{ sessionId: string }>();
  return <Navigate to={`/analytics/leadership/coaching/${sessionId ?? ""}`} replace />;
}
